import { useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { Form } from '@opendoor/cinderblocks/platform/auth/shared';
import { Flex, Button } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { showToast } from '@opendoor/bricks/complex/Toast';

import {
  QUESTIONS,
  Answers,
  defaultAnswers,
  getRouteBasedOnAnswers,
} from 'sellDirect/helpers/virtualAssessmentQuestions';
import { MultipleChoiceQuestion } from 'sellDirect/components/questionFlows/MultipleChoiceQuestion';
import { QuestionFlowLayout } from 'sellDirect/components/questionFlows/QuestionFlowLayout';
import { useUpdateOccupancyAtVa } from 'sellDirect/graphql/useUpdateOccupancyAtVa';
import { useObservability } from 'shared/helpers/observability';
import { SentOffer, VAInProgressOffer } from 'sellDirect/OfferContext';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

/**
 * Page that displays the questions the determine the default VA experience
 *
 * component-test-check-ignore storybook-check-ignore
 */
export const VAQuestionsPage: React.FC<{ offer: SentOffer | VAInProgressOffer }> = ({ offer }) => {
  const history = useHistory();
  const observability = useObservability();
  const [answers, setAnswers] = useState<Answers>(defaultAnswers);
  const { isMobile, isSmallMobile } = useDevice();
  const { addressUUID } = useQualificationContext();

  const { updateOccupancyAtVa, loading } = useUpdateOccupancyAtVa({
    onCompleted: () => {
      const redirectTo = getRouteBasedOnAnswers(answers);
      history.replace(generatePath(redirectTo, { addressUUID }));
    },
    onError: () => {
      observability.trackError('submit-occupancy-at-va-failed', undefined, {
        offerId: offer.id,
        occupancy: answers.occupancy,
      });
      showToast(
        'error',
        'There was an error submitting this answer. Please come back later',
        'cake-submit-occupancy-at-va-failed-toast',
        {
          toastOptions: { position: 'bottom-left' },
        },
      );
    },
  });

  const areAllQuestionsAnswered = Object.values(answers).every((answer) => !!answer);

  const submitButton = (
    <Button
      mt="8"
      aria-label="Submit the occupancy of my home and proceed"
      analyticsName="submit-va-occupancy-question"
      disabled={!areAllQuestionsAnswered || loading}
      loading={loading}
      type="submit"
      maxWidth={isMobile ? `calc(min(100vw - ${isSmallMobile ? '32px' : '48px'}, 436px))` : 'auto'}
      form="va-occupancy-answer-form"
    >
      Next
    </Button>
  );

  return (
    <QuestionFlowLayout
      hideFooter
      stickyBottomContent={
        isMobile && (
          <Flex justifyContent="center" mb="12px">
            {submitButton}
          </Flex>
        )
      }
    >
      <Form
        id="va-occupancy-answer-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (areAllQuestionsAnswered && answers.occupancy) {
            observability.trackEvent('click', 'submit-va-occupancy-answer', {
              occupancy: answers.occupancy,
            });
            updateOccupancyAtVa(offer.id, answers.occupancy);
          }
        }}
      >
        <Flex flexDir="column" pb={['12', null, '0']}>
          {QUESTIONS.map(({ question, questionKey, description, answers: questionOptions }) => (
            <MultipleChoiceQuestion
              key={questionKey}
              questionKey={questionKey}
              question={question}
              description={description}
              answers={questionOptions}
              disabled={loading}
              selectedValue={answers[questionKey as keyof Answers]}
              onSelection={(value) =>
                setAnswers((prevAnswers) => ({
                  ...prevAnswers,
                  [questionKey]: value as typeof questionOptions[0]['value'],
                }))
              }
              formType="radio"
            />
          ))}
        </Flex>

        {!isMobile && submitButton}
      </Form>
    </QuestionFlowLayout>
  );
};
